import type { ConnectionEntry, ContactWebGraph } from './types';

/**
 * Every edge touching `nodeId`, labelled from that node's point of view.
 * A "serves"/"refers" edge is directional (source serves/refers target), so the
 * label flips depending on whether `nodeId` sits on the source or target end.
 */
export function connectionsForNode(nodeId: string, graph: ContactWebGraph): ConnectionEntry[] {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const entries: ConnectionEntry[] = [];

  for (const edge of graph.edges) {
    const isSource = edge.source === nodeId;
    const isTarget = edge.target === nodeId;
    if (!isSource && !isTarget) continue;

    const otherId = isSource ? edge.target : edge.source;
    const other = nodesById.get(otherId);
    if (!other) continue;

    let label: ConnectionEntry['label'];
    if (edge.kind === 'serves') {
      label = isSource ? 'Serves' : 'Served by';
    } else {
      label = isSource ? 'Refers to' : 'Referred by';
    }

    entries.push({ nodeId: otherId, name: other.name, label });
  }

  return entries;
}

/**
 * The earliest date `nodeId` started working with any of its connections — the referral
 * date when the earliest edge is a referral, otherwise that edge's creation date. Returns
 * null when the node has no connections.
 */
export function workingTogetherSince(nodeId: string, graph: ContactWebGraph): string | null {
  let earliest: string | null = null;

  for (const edge of graph.edges) {
    if (edge.source !== nodeId && edge.target !== nodeId) continue;

    const since = edge.referredOn ?? edge.createdAt;
    if (earliest === null || since < earliest) earliest = since;
  }

  return earliest;
}
